import { useState } from "react";
import { toast } from "sonner";
import { api, authHeaders, setAdminToken } from "@/lib/api";
import { Save, Eye, EyeOff } from "lucide-react";

export default function PasscodeManager() {
  const [pc1, setPc1] = useState("");
  const [pc2, setPc2] = useState("");
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);

  const canSubmit =
    pc1.trim().length >= 6 && pc1 === pc2 && !saving;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSaving(true);
    try {
      const res = await api.post(
        "/admin/passcode",
        { new_passcode: pc1.trim() },
        { headers: authHeaders() }
      );
      // Refresh the stored token so subsequent requests use the new passcode
      setAdminToken(res.data.token);
      setPc1("");
      setPc2("");
      toast.success("Passcode updated. You'll use the new one from now on.");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Update failed.";
      toast.error(typeof detail === "string" ? detail : "Update failed.");
    } finally {
      setSaving(false);
    }
  };

  const mismatch = pc1.length > 0 && pc2.length > 0 && pc1 !== pc2;

  return (
    <section
      className="border p-6 sm:p-8 mb-12"
      style={{
        background: "rgba(48,23,40,0.55)",
        borderColor: "rgba(226,211,226,0.22)",
        borderRadius: "8px",
      }}
      data-testid="passcode-manager"
    >
      <div className="flex items-baseline justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="small-caps mb-2" style={{ color: "#B9929D" }}>
            Host passcode
          </div>
          <h2
            className="font-display"
            style={{ fontSize: "32px", color: "#F2EEE7", lineHeight: 1.1 }}
          >
            Change the <em style={{ color: "#DDBFCF" }}>host</em> passcode
          </h2>
        </div>
        <span className="text-xs italic" style={{ color: "#DDBFCF", opacity: 0.7 }}>
          Minimum 6 characters. Persists across deploys.
        </span>
      </div>

      <form
        onSubmit={handleSubmit}
        className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-6 items-end"
      >
        <div>
          <label className="block small-caps mb-2" style={{ color: "#B9929D" }} htmlFor="pc-new">
            New passcode
          </label>
          <div className="flex items-center gap-2">
            <input
              id="pc-new"
              data-testid="pc-new-input"
              type={show ? "text" : "password"}
              className="kynn-input"
              value={pc1}
              onChange={(e) => setPc1(e.target.value)}
              placeholder=""
              autoComplete="new-password"
            />
            <button
              type="button"
              onClick={() => setShow(!show)}
              className="p-2 opacity-70 hover:opacity-100"
              style={{ color: "#DDBFCF" }}
              aria-label={show ? "Hide passcode" : "Show passcode"}
              title={show ? "Hide" : "Show"}
              data-testid="pc-toggle-visibility"
            >
              {show ? <EyeOff size={14} /> : <Eye size={14} />}
            </button>
          </div>
        </div>

        <div>
          <label
            className="block small-caps mb-2"
            style={{ color: mismatch ? "#f0b0a8" : "#B9929D" }}
            htmlFor="pc-confirm"
          >
            Confirm {mismatch && "— doesn't match"}
          </label>
          <input
            id="pc-confirm"
            data-testid="pc-confirm-input"
            type={show ? "text" : "password"}
            className="kynn-input"
            value={pc2}
            onChange={(e) => setPc2(e.target.value)}
            placeholder=""
            autoComplete="new-password"
          />
        </div>

        <button
          type="submit"
          disabled={!canSubmit}
          data-testid="pc-save-btn"
          className="btn-primary"
          style={{ justifyContent: "center" }}
        >
          <Save size={14} /> {saving ? "Saving…" : "Update"}
        </button>
      </form>

      <p className="mt-6 text-xs italic" style={{ color: "#DDBFCF", opacity: 0.6, lineHeight: 1.6 }}>
        The passcode is stored in the database, so it persists across redeploys. Your current session is automatically switched to the new passcode after updating.
      </p>
    </section>
  );
}
